import OpenAI from 'openai';
import { supabase } from './supabase';

let openai: OpenAI | null = null;
let lastKeyCheck = 0;
const KEY_CHECK_INTERVAL = 5000; // 5 seconds

async function getOpenAIClient() {
  const now = Date.now();
  
  // Only check for new key if enough time has passed
  if (!openai || (now - lastKeyCheck > KEY_CHECK_INTERVAL)) {
    try {
      // Get API key from admin settings
      const { data, error } = await supabase
        .from('admin_settings')
        .select('openai_api_key')
        .eq('id', 1)
        .single();

      if (error) {
        console.warn('Error fetching OpenAI key:', error);
        return null;
      }

      if (!data?.openai_api_key) {
        console.warn('OpenAI API key not configured');
        return null;
      }

      // Only create new client if key changed or doesn't exist
      if (!openai || openai.apiKey !== data.openai_api_key) {
        openai = new OpenAI({
          apiKey: data.openai_api_key,
          dangerouslyAllowBrowser: true
        });
      }

      lastKeyCheck = now;
    } catch (error) {
      console.error('Error initializing OpenAI client:', error);
      return null;
    }
  }

  return openai;
}

export async function analyzeMarketData(symbol: string, historicalData: any[], aiSettings?: any) {
  try {
    const client = await getOpenAIClient();
    if (!client) {
      console.warn('OpenAI client unavailable, using rule-based analysis');
      return generateRuleBasedAnalysis(historicalData, await getMarketContext(symbol));
    }

    // Format historical data for analysis
    const formattedData = historicalData.map(d => ({
      timestamp: d.created_at,
      price: d.price,
      volume: d.volume,
      rsi: d.rsi,
      macd: d.macd,
      volume_ma: d.volume_ma
    }));

    // Get additional market context
    const marketContext = await getMarketContext(symbol);

    // Create comprehensive prompt for OpenAI
    const prompt = `Analyze the following cryptocurrency market data for ${symbol}:

Historical Data (Last ${formattedData.length} periods):
${JSON.stringify(formattedData, null, 2)}

Market Context:
${marketContext}

Technical Analysis:
- RSI values indicate: ${analyzeRSI(formattedData)}
- MACD signals: ${analyzeMACD(formattedData)}
- Volume analysis: ${analyzeVolume(formattedData)}

Based on this data, provide a detailed analysis including:
1. Current market trend (bullish, bearish, or neutral)
2. Confidence level (0-100) based on:
   - Technical indicator alignment
   - Volume confirmation
   - Market sentiment
   - Pattern strength
3. Key trading signals and potential entry/exit points
4. Time-based predictions:
   - Short term (24h): Price direction and target range
   - Mid term (7d): Market structure and key levels
   - Long term (30d): Trend projection and major support/resistance

Format the response as a JSON object with:
{
  "trend": "bullish|bearish|neutral",
  "confidence": number,
  "signals": string[],
  "prediction": string,
  "shortTerm": {
    "direction": "up|down|neutral",
    "prediction": string,
    "confidence": number
  },
  "midTerm": {
    "direction": "up|down|neutral",
    "prediction": string,
    "confidence": number
  },
  "longTerm": {
    "direction": "up|down|neutral",
    "prediction": string,
    "confidence": number
  }
}`;

    try {
      // Try with gpt-4 first
      const completion = await client.chat.completions.create({
        model: "gpt-4",
        messages: [
          {
            role: "system",
            content: "You are an expert cryptocurrency market analyst with deep knowledge of technical analysis, market psychology, and blockchain technology. Provide detailed, data-driven analysis with specific price levels and clear reasoning."
          },
          {
            role: "user",
            content: prompt
          }
        ],
        response_format: { type: "json_object" },
        temperature: 0.7,
        max_tokens: 1000
      });

      const analysis = JSON.parse(completion.choices[0].message.content);
      return processAnalysis(analysis);
    } catch (error) {
      console.warn('GPT-4 request failed, falling back to GPT-3.5:', error);
      
      // Fall back to GPT-3.5-turbo
      const completion = await client.chat.completions.create({
        model: "gpt-3.5-turbo",
        messages: [
          {
            role: "system",
            content: "You are an expert cryptocurrency market analyst with deep knowledge of technical analysis, market psychology, and blockchain technology. Provide detailed, data-driven analysis with specific price levels and clear reasoning."
          },
          {
            role: "user",
            content: prompt
          }
        ],
        response_format: { type: "json_object" },
        temperature: 0.7,
        max_tokens: 1000
      });

      const analysis = JSON.parse(completion.choices[0].message.content);
      return processAnalysis(analysis);
    }
  } catch (error) {
    console.error('Error analyzing market data:', error);
    // Return rule-based analysis as final fallback
    return generateRuleBasedAnalysis(historicalData, await getMarketContext(symbol));
  }
}

function processAnalysis(analysis: any) {
  // Validate and adjust confidence scores
  analysis.confidence = validateConfidence(analysis.confidence);
  analysis.shortTerm.confidence = validateConfidence(analysis.shortTerm.confidence);
  analysis.midTerm.confidence = validateConfidence(analysis.midTerm.confidence);
  analysis.longTerm.confidence = validateConfidence(analysis.longTerm.confidence);

  // Add timestamp
  analysis.timestamp = new Date().toISOString();

  // Store analysis in database for tracking
  storeAnalysis(analysis).catch(console.error);

  return analysis;
}

async function storeAnalysis(analysis: any) {
  try {
    const { error } = await supabase
      .from('market_analysis')
      .insert([{
        symbol: analysis.symbol,
        trend: analysis.trend,
        confidence: analysis.confidence,
        signals: analysis.signals,
        prediction: analysis.prediction,
        created_at: analysis.timestamp
      }]);

    if (error) throw error;
  } catch (error) {
    console.error('Failed to store analysis:', error);
  }
}

function validateConfidence(score: number): number {
  if (typeof score !== 'number' || isNaN(score)) return 50;
  return Math.min(Math.max(Math.round(score), 0), 100);
}

export async function getMarketSentiment(symbol: string) {
  try {
    const client = await getOpenAIClient();
    if (!client) {
      return 'Market sentiment analysis temporarily unavailable';
    }

    const prompt = `Analyze the current market sentiment for ${symbol}. Consider recent price action, trading volume, and market events. Provide a concise summary of the overall sentiment.`;

    try {
      const completion = await client.chat.completions.create({
        model: "gpt-3.5-turbo",
        messages: [
          {
            role: "system",
            content: "You are an expert in cryptocurrency market sentiment analysis. Provide clear, concise insights."
          },
          {
            role: "user",
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 150
      });

      return completion.choices[0].message.content;
    } catch (error) {
      console.error('Error getting market sentiment:', error);
      return 'Market sentiment analysis temporarily unavailable';
    }
  } catch (error) {
    console.error('Error in getMarketSentiment:', error);
    return 'Market sentiment analysis unavailable';
  }
}

// Helper functions for technical analysis
function analyzeRSI(data: any[]) {
  const rsiValues = data.map(d => d.rsi).filter(Boolean);
  if (rsiValues.length === 0) return 'Insufficient RSI data';

  const lastRSI = rsiValues[rsiValues.length - 1];
  const prevRSI = rsiValues[rsiValues.length - 2];

  if (lastRSI > 70) return 'Overbought conditions';
  if (lastRSI < 30) return 'Oversold conditions';
  if (lastRSI > prevRSI) return 'Increasing momentum';
  return 'Decreasing momentum';
}

function analyzeMACD(data: any[]) {
  const macdData = data.map(d => d.macd).filter(Boolean);
  if (macdData.length === 0) return 'Insufficient MACD data';

  const latest = macdData[macdData.length - 1];
  if (!latest) return 'Invalid MACD data';

  if (latest.histogram > 0 && latest.macd > latest.signal) {
    return 'Bullish MACD crossover';
  }
  if (latest.histogram < 0 && latest.macd < latest.signal) {
    return 'Bearish MACD crossover';
  }
  return 'No clear MACD signal';
}

function analyzeVolume(data: any[]) {
  const volumes = data.map(d => d.volume).filter(Boolean);
  if (volumes.length === 0) return 'Insufficient volume data';

  const avgVolume = volumes.reduce((a, b) => a + b, 0) / volumes.length;
  const lastVolume = volumes[volumes.length - 1];

  if (lastVolume > avgVolume * 1.5) return 'High volume activity';
  if (lastVolume < avgVolume * 0.5) return 'Low volume activity';
  return 'Normal volume levels';
}

function generateRuleBasedAnalysis(data: any[], marketContext: string) {
  // Calculate trend using technical indicators
  const rsiSignal = analyzeRSI(data);
  const macdSignal = analyzeMACD(data);
  const volumeSignal = analyzeVolume(data);

  // Determine trend direction
  let trend = 'neutral';
  let confidence = 50;
  
  if (rsiSignal.includes('Overbought')) {
    trend = 'bearish';
    confidence = 65;
  } else if (rsiSignal.includes('Oversold')) {
    trend = 'bullish';
    confidence = 65;
  }

  if (macdSignal.includes('Bullish')) {
    trend = 'bullish';
    confidence += 10;
  } else if (macdSignal.includes('Bearish')) {
    trend = 'bearish';
    confidence += 10;
  }

  if (volumeSignal.includes('High')) {
    confidence += 15;
  }

  // Ensure confidence stays within bounds
  confidence = Math.min(Math.max(confidence, 0), 100);

  // Generate analysis object
  return {
    trend,
    confidence,
    signals: [rsiSignal, macdSignal, volumeSignal],
    prediction: `Market showing ${trend} signals with ${confidence}% confidence based on technical analysis`,
    shortTerm: {
      direction: trend === 'bullish' ? 'up' : trend === 'bearish' ? 'down' : 'neutral',
      prediction: `Short-term ${trend} momentum likely to continue`,
      confidence: confidence
    },
    midTerm: {
      direction: trend === 'bullish' ? 'up' : trend === 'bearish' ? 'down' : 'neutral',
      prediction: `Mid-term outlook remains ${trend}`,
      confidence: Math.max(confidence - 10, 0)
    },
    longTerm: {
      direction: trend === 'bullish' ? 'up' : trend === 'bearish' ? 'down' : 'neutral',
      prediction: `Long-term trend analysis suggests ${trend} bias`,
      confidence: Math.max(confidence - 20, 0)
    }
  };
}
